import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import { Printer, Users, Calendar, MapPin, RefreshCw, AlertCircle } from 'lucide-react';

// Token-gated, read-only roster for instructors. No login. Noindexed.
export default function TeacherRoster() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`/api/rosters/view/${token}`);
      const body = await res.json();
      if (!res.ok) throw new Error(body.detail || 'This roster link is not valid.');
      setData(body);
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <p className="text-stone-400" data-testid="roster-loading">Loading roster…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50 p-6">
        <div className="max-w-md text-center" data-testid="roster-error">
          <AlertCircle size={36} className="mx-auto text-cafa-red mb-4" />
          <h1 className="text-2xl font-semibold text-stone-900 mb-2">Roster unavailable</h1>
          <p className="text-stone-600">{error}</p>
          <p className="text-sm text-stone-500 mt-4">
            Need help? Email <a className="underline" href="mailto:info@thecoppini.org">info@thecoppini.org</a>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 print:bg-white" data-testid="teacher-roster-page">
      <SEOHead title={`Roster — ${data.title}`} noindex />
      <style>{`@media print {
        .no-print { display: none !important; }
        body { background: #fff; }
      }`}</style>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <header className="mb-6">
          <p className="text-xs uppercase tracking-[0.18em] text-cafa-red mb-2">
            Coppini Academy of Fine Art
          </p>
          <h1 className="text-3xl sm:text-4xl font-semibold text-stone-900 leading-tight"
            data-testid="roster-title">
            {data.title}
          </h1>
          <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-sm text-stone-600">
            {data.instructor && <span>Instructor: <strong>{data.instructor}</strong></span>}
            {data.term_label && <span>{data.term_label}</span>}
            {data.schedule && (
              <span className="inline-flex items-center gap-1.5"><Calendar size={13} /> {data.schedule}</span>
            )}
            {data.location && (
              <span className="inline-flex items-center gap-1.5"><MapPin size={13} /> {data.location}</span>
            )}
          </div>
        </header>

        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div className="inline-flex items-center gap-2 text-sm font-medium text-stone-800">
            <Users size={16} className="text-cafa-red" />
            <span data-testid="roster-count">
              {data.student_count} enrolled{data.capacity ? ` of ${data.capacity} spots` : ''}
            </span>
          </div>
          <div className="flex gap-2 no-print">
            <button onClick={load}
              className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-full border border-stone-300 text-stone-700 hover:bg-white transition-colors"
              data-testid="roster-refresh-btn">
              <RefreshCw size={13} /> Refresh
            </button>
            <button onClick={() => window.print()}
              className="inline-flex items-center gap-1.5 text-sm px-4 py-2 rounded-full bg-cafa-red text-white hover:bg-red-900 transition-colors"
              data-testid="roster-print-btn">
              <Printer size={13} /> Print
            </button>
          </div>
        </div>

        {data.students.length === 0 ? (
          <div className="bg-white border border-stone-200 rounded-xl p-10 text-center text-stone-500"
            data-testid="roster-empty">
            No students are enrolled yet.
          </div>
        ) : (
          <>
            {/* Mobile cards */}
            <div className="space-y-3 sm:hidden" data-testid="roster-cards-mobile">
              {data.students.map((s, i) => (
                <div key={i} className="bg-white border border-stone-200 rounded-xl p-4">
                  <div className="flex items-baseline justify-between gap-2">
                    <p className="font-semibold text-stone-900">{s.student_name}</p>
                    <span className="text-xs text-stone-400">#{i + 1}</span>
                  </div>
                  {(s.student_age || s.student_grade) && (
                    <p className="text-sm text-stone-500 mt-0.5">
                      {s.student_age && `Age ${s.student_age}`}
                      {s.student_age && s.student_grade && ' · '}
                      {s.student_grade && `Grade ${s.student_grade}`}
                    </p>
                  )}
                  <div className="mt-3 pt-3 border-t border-stone-100 text-sm space-y-1">
                    {s.guardian_name && <p className="text-stone-700">{s.guardian_name}</p>}
                    {s.phone && <p><a className="text-cafa-red underline" href={`tel:${s.phone}`}>{s.phone}</a></p>}
                    {s.email && <p className="break-all"><a className="text-stone-600 underline" href={`mailto:${s.email}`}>{s.email}</a></p>}
                  </div>
                </div>
              ))}
            </div>

            {/* Table from sm up + print */}
            <div className="hidden sm:block bg-white border border-stone-200 rounded-xl overflow-hidden">
              <table className="w-full text-sm" data-testid="roster-table">
                <thead className="bg-stone-100 text-left text-xs uppercase tracking-wider text-stone-500">
                  <tr>
                    <th className="px-4 py-3 w-8">#</th>
                    <th className="px-4 py-3">Student</th>
                    <th className="px-4 py-3">Age / Grade</th>
                    <th className="px-4 py-3">Guardian</th>
                    <th className="px-4 py-3">Contact</th>
                  </tr>
                </thead>
                <tbody>
                  {data.students.map((s, i) => (
                    <tr key={i} className="border-t border-stone-100 align-top">
                      <td className="px-4 py-3 text-stone-400">{i + 1}</td>
                      <td className="px-4 py-3 font-medium text-stone-900">{s.student_name}</td>
                      <td className="px-4 py-3 text-stone-600">
                        {[s.student_age && `Age ${s.student_age}`, s.student_grade && `Grade ${s.student_grade}`]
                          .filter(Boolean).join(' · ') || '—'}
                      </td>
                      <td className="px-4 py-3 text-stone-700">{s.guardian_name || '—'}</td>
                      <td className="px-4 py-3 text-stone-600">
                        {s.phone && <a className="text-cafa-red underline block" href={`tel:${s.phone}`}>{s.phone}</a>}
                        {s.email && <a className="underline block break-all" href={`mailto:${s.email}`}>{s.email}</a>}
                        {!s.phone && !s.email && '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        <footer className="mt-8 pt-5 border-t border-stone-200 text-xs text-stone-400 space-y-1">
          <p>Updated live · viewed {new Date(data.generated_at).toLocaleString()}</p>
          <p className="no-print">
            This page contains student and guardian contact details. Please keep this link private.
            Questions? <a className="underline" href="mailto:info@thecoppini.org">info@thecoppini.org</a>
          </p>
        </footer>
      </div>
    </div>
  );
}
